/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
==========================================================================================*/


import Vue from 'vue'
import Router from 'vue-router'

Vue.use(Router)

export const router = new Router({
    mode: 'history',
    base: process.env.BASE_URL,
    scrollBehavior() {
        return {x: 0, y: 0}
    },
    routes: [

        {
            // =============================================================================
            // MAIN LAYOUT ROUTES
            // =============================================================================
            path: '',
            component: () => import(/* webpackChunkName: "main" *//* webpackMode: "lazy" */'./layouts/main/Main.vue'),
            children: [
                // =============================================================================
                // Theme Routes
                // =============================================================================
                {
                    path: '/',
                    name: 'Dashboard',
                    component: () => import(/* webpackChunkName: "home" *//* webpackMode: "lazy" */'./views/Home.vue'),
                    meta: {
                        rule: 'subscriber',
                        requiresAuth: true
                    }
                },
                {
                    path: '/factures',
                    name: 'Factures',
                    component: () => import(/* webpackChunkName: "bills" *//* webpackMode: "lazy" */'./views/pages/Bills.vue'),
                    meta: {
                        rule: 'subscriber',
                        requiresAuth: true
                    }
                },
                {
                    path: '/messagerie/:id?',
                    name: 'Messagerie',
                    component: () => import(/* webpackChunkName: "bills" *//* webpackMode: "lazy" */'./views/pages/Messagerie.vue'),
                    meta: {
                        rule: 'subscriber',
                        requiresAuth: true
                    }
                },
                {
                    path: '/reglement',
                    name: 'Règlement',
                    component: () => import(/* webpackChunkName: "regulation" *//* webpackMode: "lazy" */'./views/pages/Regulation.vue'),
                    meta: {
                        rule: 'subscriber',
                        requiresAuth: true
                    }
                },
                {
                    path: '/factures/:id',
                    name: 'Editer une facture',
                    props: true,
                    component: () => import(/* webpackChunkName: "editBill" *//* webpackMode: "lazy" */'./views/pages/EditBill.vue'),
                    meta: {
                        parent: 'factures',
                        rule: 'subscriber',
                        requiresAuth: true
                    }
                },
                {
                    path: '/deposer',
                    name: 'Zone de dépôt',
                    component: () => import(/* webpackChunkName: "dropFiles" *//* webpackMode: "lazy" */'./views/pages/Dropfiles.vue'),
                    meta: {
                        rule: 'subscriber',
                        requiresAuth: true
                    }
                },
                {
                    path: '/clients',
                    name: 'Clients',
                    component: () => import(/* webpackChunkName: "clients" *//* webpackMode: "lazy" */'./views/pages/Clients.vue'),
                    meta: {
                        rule: 'subscriber',
                        requiresAuth: true
                    }
                },
                {
                    path: '/clients/:id',
                    name: 'Editer un client',
                    props: true,
                    component: () => import(/* webpackChunkName: "editClient" *//* webpackMode: "lazy" */'./views/pages/EditClient.vue'),
                    meta: {
                        parent: 'clients',
                        rule: 'subscriber',
                        requiresAuth: true
                    }
                },
                {
                    path: '/profile',
                    name: 'Mon compte',
                    component: () => import(/* webpackChunkName: "profile" *//* webpackMode: "lazy" */'./views/pages/Profile.vue'),
                    meta: {
                        rule: 'subscriber',
                        requiresAuth: true
                    }
                }
            ]
        },
        // =============================================================================
        // FULL PAGE LAYOUTS
        // =============================================================================
        {
            path: '',
            component: () => import(/* webpackChunkName: "fullPage" */'@/layouts/full-page/FullPage.vue'),
            children: [
                // =============================================================================
                // PAGES
                // =============================================================================
                {
                    path: '/pages/login',
                    name: 'page-login',
                    component: () => import(/* webpackChunkName: "login" */'@/views/pages/Login.vue'),
                    meta: {
                        rule: 'subscriber'
                    }
                },
                {
                    path: '/pages/recover-password',
                    name: 'page-recover',
                    component: () => import(/* webpackChunkName: "recoverPassword" */'@/views/pages/RecoverPassword.vue'),
                    meta: {
                        rule: 'subscriber'
                    }
                },
                {
                    path: '/account-verification',
                    name: 'account-verification',
                    component: () => import(/* webpackChunkName: "recoverPassword" */'@/views/pages/AccountVerification.vue'),
                    meta: {
                        rule: 'login'
                    }
                },
                {
                    path: '/reset-password/:token',
                    name: 'page-reset',
                    component: () => import(/* webpackChunkName: "recoverPassword" */'@/views/pages/ResetPassword.vue'),
                    meta: {
                        rule: 'subscriber'
                    }
                },
                {
                    path: '/login_as',
                    name: 'login_as',
                    component: () => import(/* webpackChunkName: "recoverPassword" */'@/views/pages/LoginAs.vue'),
                    meta: {
                        rule: 'subscriber'
                    }
                },
                {
                    path: '/pages/error-404',
                    name: 'page-error-404',
                    component: () => import(/* webpackChunkName: "error404" */'@/views/pages/Error404.vue'),
                    meta: {
                        rule: 'subscriber'
                    }
                },
                {
                    path: '/pages/not-authorized',
                    name: 'page-not-authorized',
                    component: () => import(/* webpackChunkName: "notAuthorized" */'@/views/pages/NotAuthorized.vue'),
                    meta: {
                        rule: 'subscriber'
                    }
                }
            ]
        },
        // Redirect to 404 page, if no match found
        {
            path: '*',
            redirect: '/pages/error-404'
        }
    ]
})

router.afterEach(() => {
    // Remove initial loading
    const appLoading = document.getElementById('loading-bg')
    if (appLoading) {
        appLoading.style.display = 'none'
    }
})

router.beforeEach((to, from, next) => {
    const loggedIn = localStorage.getItem('user')
    const userFromStorage = (loggedIn) ? JSON.parse(loggedIn) : {}
    // console.log(userFromStorage)
    if (to.matched.some(record => record.meta.requiresAuth) && (!loggedIn || !userFromStorage.id)) {
        next('/pages/login')
    }
    if (to.path === '/pages/login' && loggedIn && userFromStorage.id) {
        next('/')
    }
    next()
})

export default router
